import { IconDoorExit } from "@tabler/icons-react";
import type { ComponentProps } from "react";
import { format, parseISO } from "date-fns";
import { ISchedule, ISlot } from "@gettactic/api";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { ReserveCheckTime } from "@/features/reserve/components/ReserveCheckin/ReserveCheckTime";
import { Button } from "@cmp-src/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@cmp-src/primitives/dropdown-menu";
import { InfoTooltip } from "@cmp-src/data-display/InfoTooltip/InfoTooltip";

type Props = {
  schedule: ISchedule;
  targetSlot: ISlot;
  onCheckin: () => void;
  onCheckout: () => void;
  loading: boolean;
  size?: ComponentProps<typeof Button>["size"];
  className?: string;
};

function getTitle(schedule: ISchedule, slot: ISlot, hasCheckedIn: boolean) {
  const start = slot.earliest_start_check_in_time;
  const end = slot.end;
  if (schedule.is_pending) {
    return `Your reservation is still pending. It needs to be approved by an admin.`;
  }
  if (!hasCheckedIn) {
    return `Check in time allowed between ${format(parseISO(start.replace("Z", "")), "h:mm a")} - ${format(
      parseISO(end.replace("Z", "")),
      "h:mm a"
    )} (${schedule.time_zone})`;
  }
  return `You have already checked in`;
}

export const ReserveCheckInButtonAdmin = ({
  schedule,
  onCheckout,
  onCheckin,
  targetSlot,
  loading,
  size = "default",
  className
}: Props) => {
  const canCheckIn = !!targetSlot?.capabilities?.can_respond_presence_check_in;
  // Walk-up occupancy is checked out externally via the API, never through
  // the app.
  const canCheckOut =
    !!targetSlot?.capabilities?.can_respond_presence_check_out &&
    !schedule.resource.is_walkup;
  const hasCheckedIn = !!targetSlot?.check_in_time;
  const hasCheckedOut = !!targetSlot?.check_out_time;
  const title = getTitle(schedule, targetSlot, hasCheckedIn);
  const disabled =
    schedule.is_pending || !canCheckIn || hasCheckedIn || loading;

  if (hasCheckedOut) {
    return (
      <div
        className="inline-flex items-center rounded-full \
      border border-disabled bg-white px-3 py-1 text-sm \
      text-neutral-400 shadow-sm"
      >
        <ReserveCheckTime type="checkout" time={targetSlot.check_out_time} />
        <IconDoorExit size={14} />
      </div>
    );
  }
  if (hasCheckedIn && !canCheckOut) {
    return (
      <div
        className="inline-flex items-center rounded-full \
      border border-disabled bg-white px-3 py-1 text-sm \
      text-neutral-400 shadow-sm"
      >
        <ReserveCheckTime type="checkin" time={targetSlot.check_in_time} />
        <IconDoorExit size={14} />
      </div>
    );
  }
  if (hasCheckedIn) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={"outline"} size={size} className={className}>
            Check Out
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent withPortal={false} className="w-[200px]">
          <DropdownMenuLabel className="flex items-center">
            Checkout
            <InfoTooltip size="sm">
              Checking out will end the reservation early, making it available
              for someone else to use.
            </InfoTooltip>
          </DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() => {
              if (!loading) {
                onCheckout();
              }
            }}
          >
            <IconDoorExit size={14} className="mr-2" />
            Check out early
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          variant={"outline"}
          size={size}
          className={className}
          disabled={disabled}
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (
              !loading &&
              !schedule.is_pending &&
              canCheckIn &&
              !hasCheckedIn
            ) {
              onCheckin();
            }
          }}
        >
          Check In
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <span className="text-base font-normal text-tertiary">{title}</span>
      </TooltipContent>
    </Tooltip>
  );
};
